import { Box } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Text } from '../../../Components/Base';
import { LoLReportModal } from '../../../Containers/LoLResultModal';
import { LoLExaminationTag } from '../../../Contexts/LoLExaminationProvider/types';
import {
  Lol_Exam_Result_Type,
  QmslolExamResult,
  useGetCareerReportByExamPaperIdQuery as useGetCareerReportByExamPaperId,
} from '../../../generated/graphql';
import useDisclosure from '../../../Utils/useDisclosure';
import AcheivementCard, { AcheivementCardLoading } from '../Components/AcheivementCard';
import { careerList } from '../constants';
import { MilestoneTags } from '../types';

type CareerListProps = {
  examPaperId?: string;
  milestones?: MilestoneTags[];
  finishedSections?: string[];
};

const ENALBED_AFTER = {
  [Lol_Exam_Result_Type.IdentityCareer]: LoLExaminationTag.STRENGTH,
  [Lol_Exam_Result_Type.PotentialIdentityCareer]: LoLExaminationTag.STRENGTH,
  [Lol_Exam_Result_Type.PreferencesCareer]: LoLExaminationTag.STRENGTH,
  [Lol_Exam_Result_Type.PersonalityCareer]: LoLExaminationTag.STRENGTH,
  [Lol_Exam_Result_Type.CapableCareer]: LoLExaminationTag.STRENGTH,
};

const isFinishedSection = (check: keyof typeof ENALBED_AFTER, finishedSections: string[]) => {
  return finishedSections.some((section) => ENALBED_AFTER[check] === section);
};

const CareerList = (props: CareerListProps) => {
  const { examPaperId = '', milestones = [], finishedSections = [] } = props;
  const { isOpen, open, close } = useDisclosure();
  const { t } = useTranslation('lolResults');
  const [currentCareer, setCurrentCareer] = useState<QmslolExamResult>();
  const [currentResultGeneralData, setCurrentResultGeneralData] = useState<{
    title: string;
    subtitle: string;
  }>({
    title: '',
    subtitle: '',
  });

  const { data: careerData, loading } = useGetCareerReportByExamPaperId({
    variables: {
      examPaperId,
    },
    fetchPolicy: 'network-only',
  });

  const careers = careerData?.qmsGetLOLExamReport.careers ?? [];

  const handleCardClick = (title: string, subtitle: string, career?: QmslolExamResult) => {
    setCurrentCareer(career ?? undefined);
    setCurrentResultGeneralData({ title, subtitle });
    open();
  };

  const handleCloseModal = () => {
    close();
  };

  const milestoneTags = milestones.flatMap((milestone) => {
    return milestone.tags;
  });

  const filterCareerList = careerList
    .filter((career) => {
      const tags = career?.tags ?? [];
      const hasTagsInMilestoneTags = tags.every((tag) => milestoneTags.includes(tag));

      return hasTagsInMilestoneTags;
    })
    .map((career) => {
      const isLocked = !isFinishedSection(
        career.type as keyof typeof ENALBED_AFTER,
        finishedSections
      );
      return {
        ...career,
        locked: isLocked,
      };
    });

  const filterCareers = careers.filter((apiCareer) => {
    return filterCareerList.some((item) => item.type === apiCareer.type && !item.locked);
  });

  return (
    <>
      <Flex flexDirection={'column'} gap={2}>
        <Box>
          <Text
            variant="h2"
            fontWeight={500}
            mr={1}
            display={'inline-block'}
            color={'lolText.primary'}
          >
            {t('CAREER')}
          </Text>
          <Text
            variant="h3"
            fontWeight={400}
            lineHeight={1.4}
            color={'lolText.secondary'}
            display={'inline-block'}
          >
            {` (${filterCareers?.length ?? 0}/${filterCareerList.length})`}
          </Text>
        </Box>
        <Flex flexDirection={'column'} gap={2}>
          {filterCareerList.map((career) => {
            const { type, title, subtitle } = career;
            const matchCareer = filterCareers?.find((careerResult) => careerResult.type === type);
            const isLocked = career.locked;

            const tags = career?.tags ?? [];
            const lastTag = tags[tags.length - 1];

            const indexOfLastTagInMilestones = milestones.findIndex((milestone) =>
              milestone.tags.includes(lastTag)
            );

            const conditionNumber =
              indexOfLastTagInMilestones !== -1
                ? indexOfLastTagInMilestones + 1
                : career?.conditionNumber;

            const careerTitle = t(`CAREER.${title}`, { ns: 'lolCommon' });
            const careerSubtitle = t(`CAREER.${subtitle}`, { ns: 'lolCommon' });

            if (loading) {
              return <AcheivementCardLoading key={type} />;
            }

            return (
              <AcheivementCard
                key={type}
                title={careerTitle}
                subtitle={careerSubtitle}
                conditionNumber={conditionNumber}
                resultType={type}
                locked={isLocked}
                onClick={() => handleCardClick(careerTitle, careerSubtitle, matchCareer)}
              />
            );
          })}
        </Flex>
      </Flex>
      <LoLReportModal
        isOpen={isOpen}
        handleClose={handleCloseModal}
        displayData={currentCareer}
        title={currentResultGeneralData?.title}
        subtitle={currentResultGeneralData?.subtitle}
      />
    </>
  );
};

export default CareerList;
